import { ApolloCache } from "@apollo/client/cache"

import { forEachNode } from "shared/lib/treeUtils"
import {
  FloorPriceTypeName,
  ProductPositionPriceTypeName,
  ProjectPriceTypename,
  RoomPriceTypeName
} from "shared/types/Price"
import {
  FloorTypename,
  ProductPositionTypename,
  ProjectChild,
  ProjectParentTypename,
  ProjectTypename,
  RoomTypename,
  isProjectFloor,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"
import {
  ProjectsAPIDeleteRequestContext,
  ProjectsAPIPositionUpdateRequestContext
} from "shared/types/ProjectMutations"

import { findParentRooms } from "entities/project-children/lib/findProjectChildParent"
import { getProjectChildren } from "entities/project-children/lib/getProjectChildren"

export function invalidateProductPositionPriceCache(
  productPositionID: string,
  cache: ApolloCache<object>
) {
  const cacheId = cache.identify({
    id: productPositionID,
    __typename: ProductPositionPriceTypeName
  })
  if (!cacheId) return
  cache.modify({
    id: cacheId,
    fields: {
      isValid: () => false
    }
  })
}

export function invalidateRoomPriceCache(
  roomID: string,
  cache: ApolloCache<object>
) {
  const cacheId = cache.identify({ id: roomID, __typename: RoomPriceTypeName })
  if (!cacheId) return
  cache.modify({
    id: cacheId,
    fields: {
      isValid: () => false
    }
  })
}

export function invalidateFloorPriceCache(
  floorID: string,
  cache: ApolloCache<object>
) {
  const cacheId = cache.identify({
    id: floorID,
    __typename: FloorPriceTypeName
  })
  if (!cacheId) return
  cache.modify({
    id: cacheId,
    fields: {
      isValid: () => false
    }
  })
}

export function invalidateProjectChildPriceCache(
  child: ProjectChild,
  cache: ApolloCache<object>
) {
  if (isProjectProductPosition(child)) {
    invalidateProductPositionPriceCache(child.id, cache)
  }
  if (isProjectRoom(child)) {
    invalidateRoomPriceCache(child.id, cache)
  }
  if (isProjectFloor(child)) {
    invalidateFloorPriceCache(child.id, cache)
  }
}

export function invalidateSubRoomsPriceCache(
  children: ProjectChild[],
  cache: ApolloCache<object>
) {
  children.forEach(child => {
    if (isProjectRoom(child)) {
      invalidateRoomPriceCache(child.id, cache)
      if (child.children) {
        invalidateSubRoomsPriceCache(child.children, cache)
      }
    }
    if (isProjectFloor(child)) {
      invalidateFloorPriceCache(child.id, cache)
      if (child.children) {
        invalidateSubRoomsPriceCache(child.children, cache)
      }
    }
  })
}

export function invalidateProjectPriceCache(
  projectID: string,
  cache: ApolloCache<object>,
  includeChildren?: boolean
) {
  const cacheId = cache.identify({
    id: projectID,
    __typename: ProjectPriceTypename
  })
  if (!cacheId) return
  cache.modify({
    id: cacheId,
    fields: {
      isValid: () => false
    }
  })

  function invalidate(node: ProjectChild) {
    invalidateProjectChildPriceCache(node, cache)
  }

  if (includeChildren) {
    const projectChildren = getProjectChildren(
      projectID,
      ProjectTypename,
      cache
    )

    forEachNode(projectChildren, invalidate)
  }
}

export function invalidateParentPricesAfterPositionUpdate({
  cache,
  context
}: {
  cache: ApolloCache<object>
  context: ProjectsAPIPositionUpdateRequestContext
}) {
  const {
    projectID,
    prevParentID,
    prevParentType,
    nextParentID,
    nextParentType
  } = context

  if (prevParentID && prevParentType === RoomTypename) {
    const prevParents = findParentRooms({
      projectID,
      cache,
      childID: prevParentID
    })
    invalidateRoomPriceCache(prevParentID, cache)
    prevParents?.forEach(parent =>
      invalidateProjectChildPriceCache(parent, cache)
    )
  }
  if (prevParentID && prevParentType === FloorTypename) {
    const prevParents = findParentRooms({
      projectID,
      cache,
      childID: prevParentID
    })
    invalidateFloorPriceCache(prevParentID, cache)
    prevParents?.forEach(parent =>
      invalidateProjectChildPriceCache(parent, cache)
    )
  }
  if (nextParentID && nextParentType === RoomTypename) {
    const nextParents = findParentRooms({
      projectID,
      cache,
      childID: nextParentID
    })
    invalidateRoomPriceCache(nextParentID, cache)
    nextParents?.forEach(parent =>
      invalidateProjectChildPriceCache(parent, cache)
    )
  }
  if (nextParentID && nextParentType === FloorTypename) {
    const nextParents = findParentRooms({
      projectID,
      cache,
      childID: nextParentID
    })
    invalidateFloorPriceCache(nextParentID, cache)
    nextParents?.forEach(parent =>
      invalidateProjectChildPriceCache(parent, cache)
    )
  }
}

export function invalidateAffectedPrices({
  projectID,
  itemID,
  itemType,
  includeChild,
  cache
}: {
  projectID: string
  itemID: string
  itemType: ProjectParentTypename | typeof ProductPositionTypename
  includeChild?: boolean
  cache: ApolloCache<object>
}) {
  invalidateProjectPriceCache(projectID, cache)

  if (itemID !== projectID) {
    const parents = findParentRooms({ projectID, childID: itemID, cache })

    if (parents) {
      parents.forEach(parent => invalidateProjectChildPriceCache(parent, cache))
    }

    if (includeChild) {
      if (itemType === ProductPositionTypename) {
        invalidateProductPositionPriceCache(itemID, cache)
      }
      if (itemType === RoomTypename) {
        invalidateRoomPriceCache(itemID, cache)
      }
      if (itemType === FloorTypename) {
        invalidateFloorPriceCache(itemID, cache)
      }
    }
  }
}

export function invalidateParentPricesAfterDeletion({
  cache,
  context
}: {
  cache: ApolloCache<object>
  context: ProjectsAPIDeleteRequestContext
}) {
  const { projectID, parentType, parentID } = context

  invalidateAffectedPrices({
    projectID,
    itemID: parentID,
    itemType: parentType,
    includeChild: true,
    cache
  })
}

export function invalidatePricesByProductType({
  projectID,
  productType,
  cache
}: {
  projectID: string
  productType: string
  cache: ApolloCache<object>
}) {
  invalidateProjectPriceCache(projectID, cache)

  const projectChildren = getProjectChildren(projectID, ProjectTypename, cache)

  function invalidate(child: ProjectChild) {
    if (
      isProjectProductPosition(child) &&
      child.variantKey.split("-")[0] === productType
    ) {
      invalidateProductPositionPriceCache(child.id, cache)
    }
    if (isProjectRoom(child)) {
      invalidateRoomPriceCache(child.id, cache)
    }
    if (isProjectFloor(child)) {
      invalidateFloorPriceCache(child.id, cache)
    }
  }

  forEachNode(projectChildren, invalidate)
}
